import React, { useEffect, useState } from 'react';
import { Heart, Star, Check, Shield, Gift, BookOpen, Sparkles, Lock, Zap, Quote, Gem, Feather, Brain, Target, Eye, ShieldCheck, Moon, Sun, Flower, Crown } from 'lucide-react';
import { Button } from './ui/Button';
import { PurchaseCard } from './ui/PurchaseCard';
import { TopBanner } from './ui/TopBanner';
import { useCurrency } from '../hooks/useCurrency';
import { AudioPlayer } from './ui/AudioPlayer';
import { CountdownTimer } from './ui/CountdownTimer';
export function LandingPage() {
  const checkoutUrl = 'https://buy.stripe.com/5kQaEX0uN7FEdBo3ojefC0a';
  const goToCheckout = () => {
    window.open(checkoutUrl, '_blank');
  };
  const ebookImage = 'https://cdn.magicpatterns.com/uploads/4NVypCqEvgUTaWmQZyk98X/hf_20260212_223854_a84ce896-3aca-484e-bacf-2d3ee372b502.png';
  const audioUrl = 'https://cdn.magicpatterns.com/uploads/p8CheZvvjmsyi4B8cLdJ2W/Bienvenida a manifiesta tu pareja ideal.mp4';
  const {
    formatPrice
  } = useCurrency();
  // Shared countdown (synced with TopBanner via localStorage)
  const COUNTDOWN_KEY = 'countdown-timer';
  const DEFAULT_TIME = 5 * 60 * 60;
  const [bonusTimeLeft, setBonusTimeLeft] = useState(() => {
    try {
      const saved = localStorage.getItem(COUNTDOWN_KEY);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (parsed > 0 && parsed <= DEFAULT_TIME) return parsed;
      }
    } catch {}
    return DEFAULT_TIME;
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setBonusTimeLeft((prev: number) => {
        const newTime = prev > 0 ? prev - 1 : 0;
        return newTime;
      });
    }, 1000);
    // Also listen for storage changes from other components
    const sync = () => {
      const saved = localStorage.getItem(COUNTDOWN_KEY);
      if (saved) setBonusTimeLeft(Math.max(0, parseInt(saved, 10)));
    };
    const interval = setInterval(sync, 5000);
    return () => {
      clearInterval(timer);
      clearInterval(interval);
    };
  }, []);
  const bonusH = Math.floor(bonusTimeLeft / 3600);
  const bonusM = Math.floor(bonusTimeLeft % 3600 / 60);
  const bonusS = bonusTimeLeft % 60;
  // Scroll reveal effect
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal, .reveal-stagger');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    reveals.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#FAF7F5]" data-id="element-1">
      {/* Top Banner */}
      <TopBanner data-id="element-2" />

      {/* Floating Particles Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10" data-id="element-3">
        <div className="absolute top-20 left-10 w-2 h-2 bg-[#C9A86A]/30 rounded-full animate-float" style={{
        animationDelay: '0s'
      }} data-id="element-4" />
        <div className="absolute top-40 right-20 w-3 h-3 bg-[#D4A5A5]/20 rounded-full animate-float" style={{
        animationDelay: '1s'
      }} data-id="element-5" />
        <div className="absolute top-60 left-1/4 w-2 h-2 bg-[#C9A86A]/20 rounded-full animate-float" style={{
        animationDelay: '2s'
      }} data-id="element-6" />
        <div className="absolute bottom-40 right-1/3 w-2 h-2 bg-[#E8D5D0]/40 rounded-full animate-float" style={{
        animationDelay: '3s'
      }} data-id="element-7" />
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 glass border-b border-[#C9A86A]/10 transition-all duration-300" data-id="element-8">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center" data-id="element-9">
          <div className="text-xl font-serif font-bold tracking-[0.2em] text-[#3A3A3A]" data-id="element-10">
            YO SOY LA MUJER{' '}
            <span className="text-gradient-gold" data-id="element-11">QUE ATRAE</span>
          </div>
          <Button onClick={goToCheckout} variant="outline" className="text-sm px-6 py-2" data-id="element-12">
            OBTENER AHORA
          </Button>
        </div>
      </nav>

      {/* 1. HERO SECTION (Text only) */}
      <header className="relative flex items-center justify-center px-6 pt-24 pb-20 overflow-hidden" data-id="element-13">
        <div className="absolute inset-0 -z-10" data-id="element-14">
          <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-[#FAF7F5] to-[#FAF7F5]" data-id="element-15" />
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-[#C9A86A]/20 to-transparent rounded-full blur-[100px] animate-pulse" style={{
          animationDuration: '4s'
        }} data-id="element-16" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#D4A5A5]/20 to-transparent rounded-full blur-[100px] animate-pulse" style={{
          animationDuration: '5s'
        }} data-id="element-17" />
        </div>

        <div className="max-w-4xl mx-auto text-center space-y-8 animate-fade-in-up" data-id="element-18">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#3A3A3A] leading-[1.15] tracking-tight" data-id="element-19">
            El sistema de 21 días para silenciar los pensamientos que te frenan
            y despertar tu energía creadora.
          </h1>

          <p className="text-2xl md:text-3xl text-[#C9A86A] font-serif italic leading-snug" data-id="element-20">
            Deja de repetir lo que no funciona. Activa lo que siempre fue tuyo.
          </p>

          <div className="w-full max-w-md mx-auto bg-[#FAF7F5] rounded-2xl p-5 border border-[#C9A86A]/30 shadow-sm" data-id="element-21">
            <p className="text-sm font-semibold text-[#C9A86A] uppercase tracking-wider mb-3 text-center" data-id="element-22">
              Antes de seguir — escuchá esto. (60 segundos)
            </p>
            <AudioPlayer src={audioUrl} data-id="element-23" />
          </div>

          <div className="flex items-center gap-3 justify-center pt-2" data-id="element-24">
            <div className="flex -space-x-3" data-id="element-25">
              {[1, 2, 3, 4].map(i => <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br from-[#C9A86A] to-[#D4A5A5] flex items-center justify-center text-white text-xs font-bold shadow-sm" data-id="element-26">
                  {String.fromCharCode(64 + i)}
                </div>)}
            </div>
            <div className="flex flex-col" data-id="element-27">
              <div className="flex items-center gap-1 text-[#C9A86A]" data-id="element-28">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-29" />)}
              </div>
              <span className="text-sm font-medium text-gray-600" data-id="element-30">
                <span className="font-bold text-[#3A3A3A]" data-id="element-31">+3,200 mujeres</span>{' '}
                ya activaron su energía
              </span>
            </div>
          </div>

          <div className="pt-4 flex flex-col sm:flex-row items-center gap-4 justify-center" data-id="element-32">
            <Button onClick={goToCheckout} variant="luxury" withSparkle className="text-lg px-10 py-5 w-full sm:w-auto shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all" data-id="element-33">
              OBTENER AHORA
            </Button>
            <div className="flex flex-col items-start" data-id="element-34">
              <span className="text-xs text-gray-500 uppercase tracking-wider font-bold" data-id="element-35">
                Oferta Especial
              </span>
              <span className="text-xl font-serif font-bold text-[#3A3A3A]" data-id="element-36">
                {formatPrice(16.99)}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* 2. TRANSFORMACIÓN TOTAL — 21 DÍAS */}
      <section className="py-20 px-6 bg-[#FAF7F5]" data-id="element-37">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center" data-id="element-38">
          <div className="reveal" data-id="element-39">
            <span className="text-[#C9A86A] font-bold tracking-[0.2em] text-sm uppercase block mb-4" data-id="element-40">
              Transformación Total
            </span>
            <h2 className="text-4xl md:text-5xl font-serif text-[#3A3A3A] mb-8" data-id="element-41">
              En 21 días comenzarás a:
            </h2>

            <ul className="space-y-6" data-id="element-42">
              {['Reconectar con tu poder interno', 'Elevar tu magnetismo natural', 'Atraer relaciones más conscientes', 'Crear oportunidades sin perseguirlas', 'Tomar decisiones desde tu valor, no desde el miedo'].map((item, i) => <li key={i} className="flex items-start gap-4 group" data-id="element-43">
                  <div className="w-6 h-6 rounded-full bg-[#C9A86A] flex items-center justify-center flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" data-id="element-44">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-45" />
                  </div>
                  <span className="text-lg text-gray-700 group-hover:text-[#3A3A3A] transition-colors" data-id="element-46">
                    {item}
                  </span>
                </li>)}
            </ul>
            <p className="mt-6 text-[#C9A86A] font-serif italic text-xl pl-10" data-id="element-47">
              Te aseguro que CREARÁS tu realidad.
            </p>
          </div>

          <div className="reveal bg-white p-8 md:p-10 rounded-[2rem] shadow-xl border border-[#E8D5D0]/50 relative overflow-hidden" data-id="element-48">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A86A]/10 rounded-bl-full" data-id="element-49" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#D4A5A5]/10 rounded-tr-full" data-id="element-50" />

            <h3 className="text-2xl font-serif font-bold text-[#3A3A3A] mb-6" data-id="element-51">
              Reprogramación Emocional y Energética
            </h3>
            <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-52">
              <p data-id="element-53">
                Es un proceso profundo. Atraemos a nuestro amor ideal, esa
                pareja con la que tanto sueñas, a soltar lo que ya no va
                contigo.
              </p>
              <p data-id="element-54">
                Aprenderás a generar más acción y productividad, más ingresos, a
                sentirte más viva.
              </p>
              <p className="font-medium text-[#3A3A3A]" data-id="element-55">
                Todo a través de un programa realizado por especialista en
                neurociencia, con audios que puedes descargar, Ebook y
                meditaciones.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. EBOOK + PROBLEM/AWAKENING */}
      <section className="py-20 px-6 bg-white relative overflow-hidden" data-id="element-56">
        <div className="max-w-6xl mx-auto" data-id="element-57">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16 reveal" data-id="element-58">
            {/* Ebook Image */}
            <div className="flex justify-center" data-id="element-59">
              <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="relative animate-float-subtle block group perspective-1000" data-id="element-60">
                <img src={ebookImage} alt="Yo Soy la Mujer que Atrae" className="w-full max-w-[420px] h-auto drop-shadow-[0_50px_100px_rgba(201,168,106,0.4)] cursor-pointer hover:scale-105 hover:rotate-1 transition-all duration-700 ease-out" data-id="element-61" />
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#C9A86A]/20 to-transparent rounded-3xl blur-3xl -z-10 scale-110" data-id="element-62" />
                {/* Bestseller Badge */}
                <div className="absolute -top-6 -right-6 bg-gradient-to-r from-[#C9A86A] to-[#D4A5A5] text-white px-5 py-2 rounded-full text-xs font-bold shadow-lg animate-pulse tracking-widest uppercase" data-id="element-63">
                  ⭐ Bestseller
                </div>
              </a>
            </div>

            {/* Problem Text */}
            <div className="text-center lg:text-left" data-id="element-64">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#3A3A3A] leading-tight mb-8" data-id="element-65">
                Nadie nos enseñó a CREAR desde nuestra energía femenina.
              </h2>
              <p className="text-lg text-gray-800 mb-8 font-medium" data-id="element-66">
                Cuando una mujer activa su energía femenina consciente, como te
                enseñará este programa, su realidad cambia y atraes:
              </p>

              <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto" data-id="element-67">
                {[{
                text: 'Relaciones poderosas',
                icon: Heart
              }, {
                text: 'Negocios exitosos',
                icon: Briefcase
              }, {
                text: 'Hábitos atómicos',
                icon: Zap
              }, {
                text: 'Desde mi energía femenina',
                icon: Sparkles
              }].map((item, i) => <div key={i} className="flex items-center gap-4 p-5 rounded-2xl bg-[#FAF7F5] border border-[#E8D5D0] hover:border-[#C9A86A] transition-colors duration-300" data-id="element-68">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-[#C9A86A]" data-id="element-69">
                      <item.icon className="w-5 h-5" data-id="element-70" />
                    </div>
                    <span className="text-lg font-serif text-[#3A3A3A]" data-id="element-71">
                      {item.text}
                    </span>
                  </div>)}
              </div>

              <div className="mt-12 text-center lg:text-left" data-id="element-72">
                <Button onClick={goToCheckout} variant="primary" withIcon data-id="element-73">
                  QUIERO ACTIVAR MI ENERGÍA
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. STORY SECTION */}
      <section className="py-24 px-6 bg-[#FAF7F5] relative overflow-hidden" data-id="element-74">
        <div className="max-w-3xl mx-auto reveal" data-id="element-75">
          <div className="text-center mb-12" data-id="element-76">
            <span className="text-[#C9A86A] font-bold tracking-[0.2em] text-sm uppercase block mb-4" data-id="element-77">
              Una historia que ya conocés
            </span>
            <h2 className="text-4xl md:text-5xl font-serif text-[#3A3A3A] leading-tight" data-id="element-78">
              Ella también hacía todo bien. Y nada cambiaba.
            </h2>
          </div>

          <div className="relative" data-id="element-79">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-[#C9A86A]/50 to-transparent mb-10" data-id="element-80" />

            <div className="space-y-8 text-xl md:text-2xl text-gray-700 font-serif leading-relaxed px-4 md:px-8" data-id="element-81">
              <p data-id="element-82">
                Se levantaba temprano. Trabajaba duro. Cuidaba a todos. Sonreía
                cuando tenía que sonreír.
              </p>
              <p data-id="element-83">
                Pero a las 11 de la noche, cuando todo estaba en silencio,
                revisaba su cuenta bancaria con miedo. Scrolleaba Instagram y
                veía a otras mujeres viajando, amando, floreciendo — y sentía
                esa punzada familiar: ¿por qué a ellas sí?
              </p>
              <p data-id="element-84">
                No era floja. No era menos inteligente. No le faltaba esfuerzo.
                Le faltaba algo que nadie le había enseñado: que el problema no
                estaba en lo que hacía. Estaba en lo que su mente repetía en
                silencio, todos los días, sin que ella lo supiera.
              </p>
              <p data-id="element-85">
                Cuando reprogramó eso — no con fuerza de voluntad sino desde el
                subconsciente — su salud cambió. Sus relaciones cambiaron. Su
                dinero cambió. No de golpe. Sino como cuando algo que siempre
                estuvo torcido finalmente encuentra su lugar.
              </p>
              <p className="font-medium text-[#3A3A3A] italic" data-id="element-86">
                Esa mujer existe. Y su historia puede ser la tuya.
              </p>
            </div>

            <div className="w-full h-px bg-gradient-to-r from-transparent via-[#C9A86A]/50 to-transparent mt-10" data-id="element-87" />
          </div>

          <div className="mt-12 text-center" data-id="element-88">
            <button onClick={goToCheckout} className="group inline-flex items-center gap-2 text-lg font-serif font-bold text-[#C9A86A] hover:text-[#D4A5A5] transition-colors" data-id="element-89">
              Quiero que mi historia cambie
              <span className="group-hover:translate-x-1 transition-transform" data-id="element-90">
                →
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* 5. THREE WORLDS */}
      <section className="py-24 px-6 bg-[#FAF7F5] border-t border-[#E8D5D0]/50" data-id="element-91">
        <div className="max-w-7xl mx-auto" data-id="element-92">
          <div className="text-center mb-16 reveal" data-id="element-93">
            <span className="text-[#C9A86A] font-bold tracking-[0.2em] text-sm uppercase" data-id="element-94">
              Tu Nueva Realidad
            </span>
            <h2 className="text-3xl md:text-5xl font-serif text-[#3A3A3A] mt-4" data-id="element-95">
              Lo que ocurre cuando reprogramás tu mente
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 reveal-stagger" data-id="element-96">
            {/* SALUD */}
            <div className="bg-white p-8 rounded-3xl shadow-lg border border-[#E8D5D0]/50 hover:-translate-y-2 transition-transform duration-500" data-id="element-97">
              <div className="w-14 h-14 rounded-2xl bg-[#FAF7F5] flex items-center justify-center mb-8 text-[#C9A86A]" data-id="element-98">
                <Heart className="w-7 h-7" data-id="element-99" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-[#3A3A3A] mb-6" data-id="element-100">
                Salud
              </h3>
              <div className="space-y-6" data-id="element-101">
                <p className="text-xl font-serif italic text-[#D4A5A5] leading-relaxed" data-id="element-102">
                  "Imagina despertarte mañana y que tu primer pensamiento no sea
                  culpa — sino ganas."
                </p>
                <p className="text-gray-600 leading-relaxed" data-id="element-103">
                  Tu cuerpo deja de ser algo que corregir. Se vuelve algo que
                  habitás con paz. El movimiento, el descanso, la alimentación —
                  dejan de ser obligaciones y se convierten en actos de amor
                  propio.
                </p>
              </div>
            </div>

            {/* AMOR */}
            <div className="bg-white p-8 rounded-3xl shadow-lg border border-[#E8D5D0]/50 hover:-translate-y-2 transition-transform duration-500" data-id="element-104">
              <div className="w-14 h-14 rounded-2xl bg-[#FAF7F5] flex items-center justify-center mb-8 text-[#C9A86A]" data-id="element-105">
                <Star className="w-7 h-7" data-id="element-106" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-[#3A3A3A] mb-6" data-id="element-107">
                Amor
              </h3>
              <div className="space-y-6" data-id="element-108">
                <p className="text-xl font-serif italic text-[#D4A5A5] leading-relaxed" data-id="element-109">
                  "Imagina estar en una conversación difícil y no ceder por
                  miedo — sino responder desde tu valor."
                </p>
                <p className="text-gray-600 leading-relaxed" data-id="element-110">
                  Dejas de tolerar lo que no merecés. No porque lo decidas con
                  la cabeza — sino porque tu subconsciente ya no acepta menos de
                  lo que sos.
                </p>
              </div>
            </div>

            {/* DINERO */}
            <div className="bg-white p-8 rounded-3xl shadow-lg border border-[#E8D5D0]/50 hover:-translate-y-2 transition-transform duration-500" data-id="element-111">
              <div className="w-14 h-14 rounded-2xl bg-[#FAF7F5] flex items-center justify-center mb-8 text-[#C9A86A]" data-id="element-112">
                <Gem className="w-7 h-7" data-id="element-113" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-[#3A3A3A] mb-6" data-id="element-114">
                Dinero
              </h3>
              <div className="space-y-6" data-id="element-115">
                <p className="text-xl font-serif italic text-[#D4A5A5] leading-relaxed" data-id="element-116">
                  "Imagina ver una oportunidad y no sabotearla. Imagina recibir
                  sin culpa."
                </p>
                <p className="text-gray-600 leading-relaxed" data-id="element-117">
                  El dinero deja de ser algo que perseguís o que huye. Se
                  convierte en energía que fluye hacia alguien que ya sabe que
                  lo merece.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. PROGRAM CONTENTS */}
      <section className="py-20 px-6 bg-white" data-id="element-118">
        <div className="max-w-6xl mx-auto" data-id="element-119">
          <div className="text-center mb-16 reveal" data-id="element-120">
            <span className="text-[#C9A86A] font-bold tracking-[0.2em] text-sm uppercase" data-id="element-121">
              ¿Qué vas a encontrar dentro?
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#3A3A3A] mt-4 mb-4" data-id="element-122">
              Este programa está estructurado en 21 días de activación
              progresiva
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16 reveal-stagger" data-id="element-123">
            {[{
            icon: Flower,
            text: 'Ejercicios diarios de reconexión femenina'
          }, {
            icon: Sparkles,
            text: 'Ritual de magnetismo y energía creadora'
          }, {
            icon: Star,
            text: 'Técnicas de manifestación alineadas con tu esencia'
          }, {
            icon: Moon,
            text: 'Trabajo de sombra emocional'
          }, {
            icon: Brain,
            text: 'Reprogramación subconsciente'
          }, {
            icon: Crown,
            text: 'Activación de autoestima magnética'
          }, {
            icon: Target,
            text: 'Plan práctico para relaciones, negocios y hábitos'
          }].map((item, index) => <div key={index} className="group relative bg-[#FAF7F5] p-8 rounded-2xl border border-[#E8D5D0]/50 hover:border-[#C9A86A] transition-all duration-500 hover:shadow-lg hover:-translate-y-1" data-id="element-124">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-6 text-[#C9A86A] shadow-sm group-hover:scale-110 transition-transform duration-500" data-id="element-125">
                  <item.icon className="w-6 h-6" strokeWidth={1.5} data-id="element-126" />
                </div>
                <p className="text-[#3A3A3A] font-medium text-lg leading-relaxed" data-id="element-127">
                  {item.text}
                </p>
              </div>)}
          </div>

          <div className="bg-[#3A3A3A] rounded-[2rem] p-8 md:p-12 text-white reveal relative overflow-hidden" data-id="element-128">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#C9A86A] rounded-full blur-[100px] opacity-20" data-id="element-129" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D4A5A5] rounded-full blur-[100px] opacity-20" data-id="element-130" />

            <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center" data-id="element-131">
              <div data-id="element-132">
                <h3 className="text-3xl font-serif font-bold mb-6 text-[#C9A86A]" data-id="element-133">
                  Además incluye:
                </h3>
                <ul className="space-y-4" data-id="element-134">
                  {['Ebook de Salud, Dinero y Amor', 'Audios para visualizaciones y Meditación', 'Diario de Gratitud', 'Diario de la Palabra De Dios para nosotras', 'Ideas creativas para emprender y generar ingresos', 'Y más…'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-135">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#C9A86A]" data-id="element-136" />
                      <span className="text-lg text-gray-200" data-id="element-137">{item}</span>
                    </li>)}
                </ul>
              </div>
              <div className="text-center" data-id="element-138">
                <p className="text-xl font-serif italic mb-8 text-gray-300" data-id="element-139">
                  "Todo lo que necesitas para tu transformación en un solo
                  lugar"
                </p>
                <Button onClick={goToCheckout} variant="luxury" withSparkle className="w-full md:w-auto" data-id="element-140">
                  ACCEDER AL CONTENIDO
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. IMAGINA ESTO (Cinematic) */}
      <section className="py-24 px-6 bg-[#1a1a1a] text-white relative overflow-hidden" data-id="element-141">
        <div className="absolute inset-0 opacity-20" data-id="element-142">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} data-id="element-143" />
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-144">
          <span className="text-[#C9A86A] font-bold tracking-[0.3em] text-sm uppercase mb-6 block reveal" data-id="element-145">
            Visualización
          </span>
          <h2 className="text-4xl md:text-6xl font-serif mb-16 reveal" data-id="element-146">
            Imagina esto…
          </h2>

          <div className="grid gap-8 mb-16 reveal-stagger" data-id="element-147">
            {['Despertarte sintiendo paz', 'Hablar desde tu verdad sin miedo', 'Atraer personas que te respetan', 'Crear dinero sin agotarte', 'Sentirte femenina, segura y poderosa'].map((item, i) => <div key={i} className="text-2xl md:text-3xl font-light text-gray-300 hover:text-white transition-colors duration-300" data-id="element-148">
                {item}
              </div>)}
          </div>

          <p className="text-xl md:text-2xl text-[#C9A86A] font-serif italic reveal" data-id="element-149">
            Eso es lo que ocurre cuando activas tu energía femenina consciente.
          </p>
        </div>
      </section>

      {/* 8. IDENTITY BRIDGE (New) */}
      <section className="py-24 px-6 bg-[#111111] text-white relative overflow-hidden border-t border-white/10" data-id="element-150">
        <div className="max-w-3xl mx-auto text-center reveal" data-id="element-151">
          <span className="text-[#C9A86A] font-bold tracking-[0.2em] text-xs uppercase block mb-6" data-id="element-152">
            Una decisión de identidad
          </span>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif mb-10 leading-tight" data-id="element-153">
            La mujer que compra esto ya tomó una decisión.
          </h2>

          <div className="max-w-[480px] mx-auto space-y-6 text-lg md:text-xl text-gray-300 font-light leading-relaxed mb-12" data-id="element-154">
            <p data-id="element-155">
              Decidió que ella importa. Que su salud, su amor propio y su
              abundancia no pueden seguir siendo lo último de la lista.
            </p>
            <p data-id="element-156">
              Decidió dejar de esperar que algo cambie solo — y empezar a ser la
              mujer que crea lo que desea.
            </p>
            <p className="font-medium text-white" data-id="element-157">
              Cuando comprás este programa no estás comprando información. Estás
              declarando quién elegís ser.
            </p>
          </div>

          <div className="w-24 h-px bg-gradient-to-r from-transparent via-[#C9A86A] to-transparent mx-auto mb-12" data-id="element-158" />

          <p className="text-2xl md:text-3xl font-serif italic text-[#C9A86A] mb-12" data-id="element-159">
            "Hoy es el día que tu versión futura recuerda."
          </p>

          <button onClick={goToCheckout} className="group inline-flex items-center gap-3 text-lg font-medium text-white hover:text-[#C9A86A] transition-colors" data-id="element-160">
            Soy esa mujer. Quiero comenzar
            <span className="group-hover:translate-x-1 transition-transform" data-id="element-161">
              →
            </span>
          </button>
        </div>
      </section>

      {/* 9. SALES/OFFER SECTION */}
      <section id="sales-section" className="py-20 px-6 bg-gradient-to-b from-white to-[#FAF7F5]" data-id="element-162">
        <div className="max-w-6xl mx-auto" data-id="element-163">
          <div className="text-center mb-12 reveal" data-id="element-164">
            <div className="inline-block px-6 py-2 rounded-full bg-gradient-to-r from-[#C9A86A] to-[#D4A5A5] text-white text-sm font-bold uppercase tracking-widest shadow-lg animate-pulse mb-8" data-id="element-165">
              🌟 Oferta especial por tiempo limitado
            </div>
            <CountdownTimer data-id="element-166" />
          </div>

          <div className="reveal grid lg:grid-cols-2 gap-12 items-center mt-12" data-id="element-167">
            {/* Floating Ebook */}
            <div className="flex flex-col items-center space-y-8" data-id="element-168">
              <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="relative animate-float-subtle block perspective-1000" data-id="element-169">
                <img src={ebookImage} alt="Ebook" className="w-full max-w-[420px] drop-shadow-[0_50px_100px_rgba(201,168,106,0.4)] cursor-pointer hover:scale-105 hover:rotate-1 transition-all duration-700" data-id="element-170" />
              </a>
            </div>

            {/* Purchase Card */}
            <div className="w-full" data-id="element-171">
              <PurchaseCard title="YO SOY una mujer que atrae" subtitle="Comienza a Manifestar la Vida que Mereces" featured={true} className="h-full transform hover:scale-[1.02] transition-transform duration-500" data-id="element-172" />
            </div>
          </div>
        </div>
      </section>

      {/* 10. GUARANTEE */}
      <section className="py-12 px-6 bg-[#FAF7F5]" data-id="element-173">
        <div className="max-w-3xl mx-auto reveal" data-id="element-174">
          <div className="bg-[#FFFBF5] border border-[#C9A86A]/30 rounded-2xl p-8 text-center shadow-lg relative overflow-hidden group hover:shadow-xl transition-all duration-500" data-id="element-175">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#C9A86A] to-[#D4A5A5]" data-id="element-176" />
            <div className="w-16 h-16 bg-[#C9A86A]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-500" data-id="element-177">
              <ShieldCheck className="w-8 h-8 text-[#C9A86A]" data-id="element-178" />
            </div>
            <h3 className="text-2xl font-serif font-bold text-[#3A3A3A] mb-3" data-id="element-179">
              Garantía de 7 días
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-180">
              Si el programa no resuena contigo, puedes solicitar reembolso
              completo. Sin preguntas.
            </p>
          </div>
        </div>
      </section>

      {/* 11. BONUSES — Countdown */}
      <section className="py-6 px-6 bg-[#1a1a1a] text-white" data-id="element-181">
        <div className="max-w-4xl mx-auto text-center" data-id="element-182">
          <p className="text-sm md:text-base font-bold tracking-widest uppercase text-white/80 mb-4" data-id="element-183">
            ⚠️ Estos bonos desaparecen en:
          </p>
          <div className="flex items-center justify-center gap-3 md:gap-6" data-id="element-184">
            <div className="flex flex-col items-center" data-id="element-185">
              <span className="text-4xl md:text-5xl font-serif font-bold text-[#C9A86A] tabular-nums" data-id="element-186">
                {bonusH.toString().padStart(2, '0')}
              </span>
              <span className="text-[10px] text-white/60 uppercase tracking-widest mt-1" data-id="element-187">
                Horas
              </span>
            </div>
            <span className="text-3xl text-[#C9A86A] font-serif mb-4" data-id="element-188">:</span>
            <div className="flex flex-col items-center" data-id="element-189">
              <span className="text-4xl md:text-5xl font-serif font-bold text-[#C9A86A] tabular-nums" data-id="element-190">
                {bonusM.toString().padStart(2, '0')}
              </span>
              <span className="text-[10px] text-white/60 uppercase tracking-widest mt-1" data-id="element-191">
                Minutos
              </span>
            </div>
            <span className="text-3xl text-[#C9A86A] font-serif mb-4" data-id="element-192">:</span>
            <div className="flex flex-col items-center" data-id="element-193">
              <span className="text-4xl md:text-5xl font-serif font-bold text-[#C9A86A] tabular-nums" data-id="element-194">
                {bonusS.toString().padStart(2, '0')}
              </span>
              <span className="text-[10px] text-white/60 uppercase tracking-widest mt-1" data-id="element-195">
                Segundos
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 11b. BONUSES — Cards */}
      <section className="py-20 px-6 bg-white" data-id="element-196">
        <div className="max-w-5xl mx-auto" data-id="element-197">
          {/* Header */}
          <div className="text-center mb-14 reveal" data-id="element-198">
            <span className="text-[#C9A86A] font-bold tracking-[0.2em] text-sm uppercase block mb-3" data-id="element-199">
              Solo por hoy — incluidos gratis
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#3A3A3A] leading-tight" data-id="element-200">
              3 Bonos Exclusivos valorados en $59.97 — tuyos{' '}
              <span className="text-[#D4A5A5]" data-id="element-201">sin costo adicional</span>
            </h2>
            <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto" data-id="element-202">
              Al acceder al programa hoy, estos tres recursos se agregan
              automáticamente a tu cuenta. Sin costo extra. Sin condiciones.
            </p>
          </div>

          {/* 3 Bonus Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-12 reveal-stagger" data-id="element-203">
            {[{
            num: '01',
            icon: '🎧',
            title: 'Audio de Reprogramación — "Yo Merezco Recibir"',
            desc: '20 minutos nocturnos que reprograman la creencia que más te bloquea: que recibir amor, dinero y paz es algo que merecés.'
          }, {
            num: '02',
            icon: '🧘‍♀️',
            title: 'Meditación — "Corta el Patrón"',
            desc: '15 minutos de intervención inmediata para el momento exacto en que el pensamiento negativo o el patrón repetitivo aparece.'
          }, {
            num: '03',
            icon: '📋',
            title: 'Guía PDF — "El Código de tu Energía Femenina"',
            desc: 'Protocolo de 7 pasos para activar tu energía creadora intencionalmente en amor, salud y dinero — desde el día 1.'
          }].map((bonus, index) => <div key={index} className="relative bg-white border border-[#C9A86A]/20 rounded-2xl p-8 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-500 flex flex-col items-center text-center" data-id="element-204">
                <span className="text-xs font-bold text-[#C9A86A] tracking-[0.3em] uppercase mb-5" data-id="element-205">
                  BONO {bonus.num}
                </span>
                <div className="text-5xl mb-5" data-id="element-206">{bonus.icon}</div>
                <h3 className="text-xl font-serif font-bold text-[#3A3A3A] mb-3 leading-snug" data-id="element-207">
                  {bonus.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-1" data-id="element-208">
                  {bonus.desc}
                </p>
                <div className="w-full h-px bg-[#E8D5D0] mb-4" data-id="element-209" />
                <span className="text-gray-400 line-through text-lg mb-1" data-id="element-210">
                  $19.99
                </span>
                <span className="text-[#D4A5A5] font-bold text-sm tracking-widest uppercase" data-id="element-211">
                  INCLUIDO GRATIS HOY
                </span>
              </div>)}
          </div>

          {/* Value Bar */}
          <div className="bg-[#3A3A3A] rounded-2xl px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 mb-10 reveal" data-id="element-212">
            <span className="text-white font-medium text-lg" data-id="element-213">
              Valor total de los bonos
            </span>
            <div className="flex items-center gap-3" data-id="element-214">
              <span className="text-gray-400 line-through text-xl" data-id="element-215">$59.97</span>
              <span className="text-2xl font-bold text-[#C9A86A]" data-id="element-216">
                → GRATIS hoy
              </span>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center reveal" data-id="element-217">
            <Button onClick={goToCheckout} variant="luxury" withSparkle className="text-lg px-10 py-5 shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all" data-id="element-218">
              Quiero el programa + los 3 bonos →
            </Button>
            <p className="text-sm text-gray-500 mt-4" data-id="element-219">
              Los bonos se agregan automáticamente. Solo disponibles con esta
              oferta.
            </p>
          </div>
        </div>
      </section>

      {/* 12. TESTIMONIALS */}
      <section className="py-20 px-6 bg-[#FAF7F5]" data-id="element-220">
        <div className="max-w-6xl mx-auto" data-id="element-221">
          <div className="text-center mb-16 reveal" data-id="element-222">
            <span className="text-[#C9A86A] font-semibold tracking-[0.2em] text-sm uppercase" data-id="element-223">
              Historias Reales
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#3A3A3A] mt-4" data-id="element-224">
              Mujeres que decidieron elegirse
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 reveal-stagger" data-id="element-225">
            {[{
            quote: 'Revisaba mi cuenta bancaria con miedo antes de pagar el supermercado. Trabajaba 10 horas al día y seguía sintiendo que nunca era suficiente. Después de la Fase 2 del programa entendí que el problema no era mi esfuerzo — era lo que creía que merecía tener. Un mes después cerré el mejor contrato de mi vida. No cambié mi estrategia. Cambié lo que creía de mí misma.',
            name: 'Luciana G.',
            location: 'Buenos Aires',
            highlight: 'DINERO & MERECIMIENTO'
          }, {
            quote: 'El cuarto hombre consecutivo que no me elegía de verdad. Me decía que yo elegía mal — pero en el fondo sabía que algo en mí los atraía. Los audios nocturnos me parecían demasiado simples para funcionar. Pero al día 14 mis reacciones cambiaron solas. Hoy estoy con alguien que me elige todos los días. Sin que yo lo persiga.',
            name: 'Carolina M.',
            location: 'Ciudad de México',
            highlight: 'PATRÓN ROTO'
          }, {
            quote: 'Tenía la app de running instalada hace dos años. Sin usarla. Me prometía empezar el lunes desde hacía meses. No era falta de disciplina — era un bloqueo que no podía ver. Después de la Fase 1 entendí de dónde venía. En la Fase 2 empecé a moverme, a comer mejor — sin fuerza de voluntad. Simplemente surgió desde adentro.',
            name: 'Valentina R.',
            location: 'Lima',
            highlight: 'SALUD & HÁBITOS'
          }, {
            quote: 'Abría Instagram y en 30 segundos ya me sentía menos. Menos exitosa, menos linda, menos suficiente. Lo hacía todos los días y todos los días me hacía daño. El programa no me dijo que dejara de comparar — reprogramó la razón por la que me comparaba. Eso es lo que ningún consejo de autoayuda había logrado.',
            name: 'Mariana L.',
            location: 'Bogotá',
            highlight: 'FIN DE LA COMPARACIÓN'
          }, {
            quote: 'Le puse play al primer audio con los ojos en blanco. Soy terapeuta — no creo fácilmente en estas cosas. Pero al día 21 algo había cambiado en cómo me relacionaba conmigo misma. No sé explicarlo de forma clínica. Solo sé que lo que cambié desde adentro se empezó a ver afuera.',
            name: 'Sofía G.',
            location: 'Santiago',
            highlight: 'ESCÉPTICA CONVERTIDA'
          }, {
            quote: 'En tres semanas: empecé a ir al gimnasio sin que nadie me obligara, tuve la conversación que venía postergando con mi pareja hace seis meses, y acepté un proyecto que antes hubiera rechazado por miedo. No hice nada diferente en lo externo. Reprogramé lo interno — y lo externo se reorganizó solo.',
            name: 'Andrea P.',
            location: 'Medellín',
            highlight: 'LAS TRES ÁREAS'
          }].map((testimonial, index) => <div key={index} className="bg-white p-8 rounded-2xl border border-[#E8D5D0]/50 shadow-sm hover:shadow-lg transition-all duration-500 card-luxury relative" data-id="element-226">
                <Quote className="w-8 h-8 text-[#E8D5D0] absolute top-6 right-6" data-id="element-227" />
                <span className="inline-block px-3 py-1 bg-[#C9A86A]/10 text-[#C9A86A] text-xs font-semibold rounded-full mb-4" data-id="element-228">
                  {testimonial.highlight}
                </span>
                <p className="text-gray-700 text-base leading-relaxed mb-6 italic" data-id="element-229">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8D5D0]/30 pt-4" data-id="element-230">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A86A] to-[#D4A5A5] flex items-center justify-center text-white font-serif font-bold text-sm" data-id="element-231">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div data-id="element-232">
                    <p className="font-semibold text-[#3A3A3A] text-sm" data-id="element-233">
                      {testimonial.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-234">
                      {testimonial.location}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 13. AUTHOR SECTION */}
      <section className="py-16 px-6 bg-white border-y border-[#C9A86A]/10" data-id="element-235">
        <div className="max-w-3xl mx-auto text-center reveal" data-id="element-236">
          <div className="w-16 h-1 bg-[#C9A86A] mx-auto mb-6 rounded-full" data-id="element-237" />
          <h3 className="text-2xl font-serif font-bold text-[#3A3A3A] mb-2" data-id="element-238">
            Creado por Danielle E. S.
          </h3>
          <p className="text-gray-600 italic text-lg" data-id="element-239">
            12 años acompañando procesos de crecimiento personal.
          </p>
          <div className="w-16 h-1 bg-[#C9A86A] mx-auto mt-6 rounded-full" data-id="element-240" />
        </div>
      </section>

      {/* 14. FINAL CTA / EMOTIONAL CLOSE */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#FAF7F5] to-white text-center relative overflow-hidden" data-id="element-241">
        <div className="absolute inset-0 opacity-30" data-id="element-242">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#C9A86A] rounded-full blur-[150px]" data-id="element-243" />
        </div>

        <div className="max-w-3xl mx-auto relative z-10 reveal" data-id="element-244">
          <h2 className="text-4xl md:text-6xl font-serif text-[#3A3A3A] leading-tight mb-12" data-id="element-245">
            Dentro de un año te vas a mirar al espejo.
          </h2>

          <div className="space-y-8 text-xl md:text-2xl text-gray-700 font-serif leading-relaxed mb-16" data-id="element-246">
            <p className="font-medium" data-id="element-247">
              La pregunta no es si vas a cambiar. La pregunta es si vas a ser
              vos quien decida cómo.
            </p>
            <p data-id="element-248">
              Dentro de un año, ese espejo puede reflejar a la misma mujer que
              hoy scrollea a las 11pm sintiéndose menos. Que sigue dando sin
              recibir. Que sigue postergando su bienestar para después.
            </p>
            <p data-id="element-249">
              O puede reflejar a la mujer que un día decidió que ya era
              suficiente. Que se eligió. Que reprogramó lo que nadie le había
              enseñado a cambiar.
            </p>
          </div>

          <p className="text-3xl md:text-4xl font-serif italic text-[#C9A86A] mb-12" data-id="element-250">
            Esa decisión se toma hoy. No mañana.
          </p>

          <div className="max-w-md mx-auto" data-id="element-251">
            <Button onClick={goToCheckout} variant="luxury" withSparkle className="text-xl px-12 py-6 w-full shadow-2xl hover:-translate-y-1 transition-all mb-6" data-id="element-252">
              Quiero ser esa mujer — empiezo hoy →
            </Button>

            <p className="text-sm font-medium text-gray-500 tracking-wide" data-id="element-253">
              Acceso inmediato · {formatPrice(16.99)} · Garantía de 7 días sin
              riesgo
            </p>
          </div>

          <div className="pt-20 mt-20 border-t border-[#E8D5D0]" data-id="element-254">
            <p className="text-sm text-gray-400" data-id="element-255">
              © 2024 Yo Soy la Mujer que Atrae. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </section>
    </div>;
}
// Helper component for icons used in the grid
function Briefcase(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-256">
      <rect width="20" height="14" x="2" y="7" rx="2" ry="2" data-id="element-257" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" data-id="element-258" />
    </svg>;
}